import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { MenuItemComponent } from './MenuItem';
import { useOutsideClick } from './useOutsideClick';
import { useMenuKeyboard } from './useMenuKeyboard';
/** Padding from viewport edges so menus don't touch the browser chrome. */
const VIEWPORT_MARGIN = 8;
const SUBMENU_GAP = 4;
/**
 * Compute fixed-position style for a submenu based on its trigger element's
 * bounding rect. Flips horizontally if the submenu would overflow the right
 * edge, and constrains max-height to the available vertical space.
 */
function computeSubmenuPosition(triggerEl, submenuEl) {
    const rect = triggerEl.getBoundingClientRect();
    const submenuRect = submenuEl.getBoundingClientRect();
    let left = rect.right + SUBMENU_GAP;
    let top = rect.top;
    // Flip to the left if it would overflow the right edge
    if (left + submenuRect.width > window.innerWidth - VIEWPORT_MARGIN) {
        left = rect.left - submenuRect.width - SUBMENU_GAP;
    }
    // Constrain height to available space below the top position
    const availableBelow = window.innerHeight - top - VIEWPORT_MARGIN;
    const maxHeight = Math.max(120, availableBelow);
    // If the submenu is taller than the available space, shift it up
    // but only as far as needed (and never above the viewport top)
    if (submenuRect.height > availableBelow) {
        top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - Math.min(submenuRect.height, maxHeight));
    }
    return {
        top,
        left,
        maxHeight,
        overflowY: 'auto',
    };
}
/**
 * Walk up the DOM from `el` and return the bounding rect of the nearest
 * ancestor that clips overflow (overflow-y is hidden, auto, or scroll).
 * Falls back to the viewport rect if no clipping ancestor is found.
 */
function getClipBounds(el) {
    let current = el.parentElement;
    while (current) {
        const ov = getComputedStyle(current).overflowY;
        if (ov === 'hidden' || ov === 'auto' || ov === 'scroll') {
            const r = current.getBoundingClientRect();
            return { top: r.top, bottom: r.bottom };
        }
        current = current.parentElement;
    }
    return { top: 0, bottom: window.innerHeight };
}
/**
 * Compute max-height for a menu so it fits within its nearest clipping
 * ancestor (or the viewport). Works for both upward and downward menus by
 * measuring how much the unconstrained menu overflows the clip bounds and
 * subtracting that from its natural height.
 */
function computeMenuMaxHeight(menuEl) {
    const rect = menuEl.getBoundingClientRect();
    const bounds = getClipBounds(menuEl);
    const overflowTop = Math.max(0, (bounds.top + VIEWPORT_MARGIN) - rect.top);
    const overflowBottom = Math.max(0, rect.bottom - (bounds.bottom - VIEWPORT_MARGIN));
    const totalOverflow = overflowTop + overflowBottom;
    if (totalOverflow > 0) {
        const maxHeight = Math.max(120, rect.height - totalOverflow);
        return { maxHeight, overflowY: 'auto' };
    }
    // Menu fits — no constraint needed
    return {};
}
/** Wrapper for a single submenu item + its child Menu, with ref-based positioning. */
function SubmenuSlot({ item, isOpen, onToggle, onClose, parentMenuRef, }) {
    const wrapperRef = useRef(null);
    const submenuRef = useRef(null);
    const [fixedStyle, setFixedStyle] = useState({ visibility: 'hidden' });
    // Compute position after the submenu mounts / becomes visible
    useEffect(() => {
        if (!isOpen) {
            // Reset so the next open hides until repositioned, avoiding a flash
            // at the previous position.
            setFixedStyle({ visibility: 'hidden' });
            return;
        }
        // Wait one frame for the submenu to render and have dimensions
        const raf = requestAnimationFrame(() => {
            const triggerEl = wrapperRef.current?.querySelector('[data-menu-item]');
            const submenuEl = submenuRef.current;
            if (triggerEl && submenuEl) {
                setFixedStyle(computeSubmenuPosition(triggerEl, submenuEl));
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [isOpen]);
    return (_jsxs("div", { ref: wrapperRef, children: [_jsx(MenuItemComponent, { item: item, focused: false, tabIndex: -1, onClick: onToggle, hasOpenSubmenu: isOpen }), _jsx(Menu, { ref: submenuRef, items: item.children, isOpen: isOpen, onClose: onClose, ariaLabel: typeof item.label === 'string' ? item.label : 'Submenu', isSubmenu: true, onSubmenuClose: () => {
                    onClose();
                    // Return focus to the submenu trigger
                    const trigger = parentMenuRef.current?.querySelector(`[data-menu-item-id="${item.id}"]`);
                    trigger?.focus();
                }, layout: item.layout, gridColumns: item.gridColumns, fixedStyle: fixedStyle })] }));
}
export const Menu = React.memo(React.forwardRef(function Menu({ items, isOpen, onClose, ariaLabel, triggerRef, className, layout, gridColumns, isSubmenu, onSubmenuClose, fixedStyle, }, ref) {
    const localRef = useRef(null);
    const menuRef = ref ?? localRef;
    const [openSubmenuId, setOpenSubmenuId] = useState(null);
    const [dynamicStyle, setDynamicStyle] = useState({});
    const openSubmenuForItem = useCallback((itemId) => {
        setOpenSubmenuId(itemId);
    }, []);
    const handleKeyDown = useMenuKeyboard({
        menuRef,
        onClose,
        triggerRef,
        isSubmenu,
        onSubmenuClose,
        openSubmenuForItem,
    });
    // Build refs array for outside click
    const outsideClickRefs = useMemo(() => {
        const refs = [menuRef];
        if (triggerRef)
            refs.push(triggerRef);
        return refs;
    }, [triggerRef, menuRef]);
    useOutsideClick(outsideClickRefs, isOpen, onClose);
    // Compute dynamic max-height for top-level menus after mount, based on
    // actual viewport position. Submenus get their constraints from fixedStyle
    // computed by SubmenuSlot instead.
    useEffect(() => {
        if (!isOpen || isSubmenu || !menuRef.current)
            return;
        const raf = requestAnimationFrame(() => {
            if (!menuRef.current)
                return;
            setDynamicStyle(computeMenuMaxHeight(menuRef.current));
        });
        return () => cancelAnimationFrame(raf);
    }, [isOpen, isSubmenu, menuRef]);
    // Focus first non-disabled item on open
    useEffect(() => {
        if (!isOpen || !menuRef.current)
            return;
        // Small delay to let the DOM render
        const timer = requestAnimationFrame(() => {
            if (!menuRef.current)
                return;
            const allItems = menuRef.current.querySelectorAll('[data-menu-item]');
            // Only direct items
            const directItems = Array.from(allItems).filter((el) => el.closest('[role="menu"]') === menuRef.current);
            const firstEnabled = directItems.find((el) => el.getAttribute('aria-disabled') !== 'true');
            if (firstEnabled) {
                // Set roving tabindex
                for (const el of directItems) {
                    el.setAttribute('tabindex', '-1');
                }
                firstEnabled.setAttribute('tabindex', '0');
                firstEnabled.focus();
            }
        });
        return () => cancelAnimationFrame(timer);
    }, [isOpen, menuRef]);
    // Reset submenu state when menu closes
    useEffect(() => {
        if (!isOpen) {
            setOpenSubmenuId(null);
            setDynamicStyle({});
        }
    }, [isOpen]);
    if (!isOpen)
        return null;
    const classNames = [
        'hekate-menu',
        isSubmenu ? 'hekate-menu--submenu' : '',
        layout === 'grid' ? 'hekate-menu--grid' : '',
        className || '',
    ]
        .filter(Boolean)
        .join(' ');
    const style = { ...dynamicStyle, ...fixedStyle };
    if (layout === 'grid' && gridColumns) {
        style.gridTemplateColumns = `repeat(${gridColumns}, 1fr)`;
    }
    return (_jsx("div", { ref: menuRef, role: "menu", "aria-label": ariaLabel, className: classNames, style: style, onKeyDown: handleKeyDown, children: items.map((item) => {
            if (item.type === 'separator') {
                return (_jsx("div", { className: "hekate-menu-separator", role: "separator" }, item.id));
            }
            if (item.type === 'label') {
                return (_jsx("div", { className: "hekate-menu-label", role: "presentation", children: item.text }, item.id));
            }
            if (item.type === 'submenu') {
                const isSubmenuOpen = openSubmenuId === item.id;
                return (_jsx(SubmenuSlot, { item: item, isOpen: isSubmenuOpen, onToggle: () => setOpenSubmenuId(isSubmenuOpen ? null : item.id), onClose: () => setOpenSubmenuId(null), parentMenuRef: menuRef }, item.id));
            }
            return (_jsx(MenuItemComponent, { item: item, focused: false, tabIndex: -1, onClick: () => {
                    if ('onClick' in item && item.onClick) {
                        item.onClick();
                    }
                } }, item.id));
        }) }));
}));
